import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../i18n';
import type { Mailbox } from '../api/types';
import { buildTree, displayLabel, flatten, normaliseName, pathOf } from '../mail/folders';

/*
    Move-to-folder. A searchable list rather than a nested menu: with a real
    folder tree, walking submenus to reach Projects/2026/faturalar is slower than
    typing three letters, and typing works the same however deep the tree gets.
*/

export default function FolderPicker({
    mailboxes,
    excludeId,
    onPick,
    onCreate,
    onClose,
    anchor,
}: {
    mailboxes: Mailbox[];
    /** The folder we're already in — moving there is a no-op worth hiding. */
    excludeId?: string;
    onPick: (mailboxId: string) => void;
    onCreate?: (name: string) => void;
    onClose: () => void;
    anchor: { x: number; y: number };
}) {
    const { t } = useT();
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => inputRef.current?.focus(), []);

    const entries = useMemo(() => {
        const ordered = flatten(buildTree(mailboxes), new Set());
        return ordered
            .map((n) => ({ mailbox: n.mailbox, depth: n.depth, path: pathOf(mailboxes, n.mailbox.id) }))
            .filter((e) => e.mailbox.id !== excludeId);
    }, [mailboxes, excludeId]);

    const shown = useMemo(() => {
        const q = normaliseName(query);
        if (!q) return entries;
        // Matching the whole path means "proj fat" finds Projects/Faturalar.
        return entries.filter((e) => q.split(' ').every((part) => normaliseName(e.path).includes(part)));
    }, [entries, query]);

    const exact = shown.some((e) => normaliseName(e.mailbox.name) === normaliseName(query));
    const canCreate = Boolean(onCreate) && query.trim().length > 0 && !exact;
    const total = shown.length + (canCreate ? 1 : 0);

    useEffect(() => setActive(0), [query]);

    useEffect(() => {
        listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const choose = (index: number) => {
        if (canCreate && index === shown.length) {
            onCreate?.(query.trim());
        } else {
            const hit = shown[index];
            if (hit) onPick(hit.mailbox.id);
        }
        onClose();
    };

    return (
        <>
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80 }} />
            <div
                style={{
                    position: 'fixed',
                    left: Math.max(8, Math.min(anchor.x, window.innerWidth - 288)),
                    top: Math.max(8, Math.min(anchor.y, window.innerHeight - 320)),
                    zIndex: 81,
                    width: 280,
                    padding: 6,
                    background: 'var(--surface)',
                    border: '1px solid var(--border2)',
                    borderRadius: 'var(--r-card)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
                }}
            >
                <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setActive((a) => (total ? (a + 1) % total : 0));
                        } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setActive((a) => (total ? (a - 1 + total) % total : 0));
                        } else if (e.key === 'Enter') {
                            e.preventDefault();
                            choose(active);
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            onClose();
                        }
                    }}
                    placeholder={t('folders.moveTo')}
                    spellCheck={false}
                    style={{
                        width: '100%',
                        height: 30,
                        padding: '0 9px',
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-btn)',
                        outline: 'none',
                        fontSize: 'var(--fs-sm)',
                    }}
                />

                <div ref={listRef} style={{ maxHeight: 250, overflowY: 'auto', marginTop: 5 }}>
                    {shown.map((e, i) => (
                        <button
                            key={e.mailbox.id}
                            data-active={i === active}
                            onMouseEnter={() => setActive(i)}
                            onClick={() => choose(i)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                width: '100%',
                                padding: '6px 8px',
                                paddingLeft: 8 + (query ? 0 : e.depth * 12),
                                textAlign: 'left',
                                background: i === active ? 'var(--accent-bg)' : 'transparent',
                                border: 'none',
                                borderRadius: 7,
                                color: 'var(--text-dim)',
                                fontSize: 'var(--fs-sm)',
                            }}
                        >
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {query ? e.path : displayLabel(e.mailbox, (role) => t(`folders.role.${role}` as const))}
                            </span>
                        </button>
                    ))}

                    {canCreate ? (
                        <button
                            data-active={active === shown.length}
                            onMouseEnter={() => setActive(shown.length)}
                            onClick={() => choose(shown.length)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                width: '100%',
                                padding: '6px 8px',
                                textAlign: 'left',
                                background: active === shown.length ? 'var(--accent-bg)' : 'transparent',
                                border: 'none',
                                borderRadius: 7,
                                color: 'var(--accent-strong)',
                                fontSize: 'var(--fs-sm)',
                            }}
                        >
                            <span style={{ width: 12, textAlign: 'center' }}>+</span>
                            {t('folders.createNamed', { name: query.trim() })}
                        </button>
                    ) : null}

                    {!total ? (
                        <div style={{ padding: '10px 8px', fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>{t('folders.noMatch')}</div>
                    ) : null}
                </div>
            </div>
        </>
    );
}
